import { compact, isInteger, isString, omit } from "lodash";
import moment from "moment";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupDelivery,
  AssessmentGroupProviders,
  AssessmentQuestionResponses,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  CompetencyAssessmentType,
  ComplianceProgramAppliesTo,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  ComplianceProgramState,
  ComplianceProgramStep,
  GetComplianceProgramEditQuery,
  Maybe,
  OrganizationTalentIndicatorFragment,
  SaveComplianceProgramMutationVariables,
  TalentIndicatorQuestionSettingsNode,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import {
  convertEvaluationPeriodTypeToPeriod,
  getEvaluationPeriodType,
} from "./helpers";
import {
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramFormStep,
  ProgramFormStepDownwardReview,
  ProgramFormStepPeerReview,
  ProgramFormStepSurvey,
  ProgramFormStepUpwardReview,
  ProgramFormTalentIndicatorReviewConfig,
} from "./types";

export const getDefaultProgramFormStep = (
  stepType: ComplianceProgramStep,
): ProgramFormStep => {
  if (stepType === ComplianceProgramStep.PeerReview) {
    return {
      stepType: ComplianceProgramStep.PeerReview,
      questionSetIds: [],
      anonymity: AssessmentGroupAnonymity.NotAnonymous,
      providers: AssessmentGroupProviders.Default,
      roleBasedReviewConfig: {
        enabledForCurrentRole: false,
        enabledForNextRole: false,
        enabledSelfReviewForNextRole: false,
        competencyAssessmentType: CompetencyAssessmentType.CriteriaCombined,
        nextRoleCompetencyAssessmentType:
          CompetencyAssessmentType.CriteriaCombined,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
      },
      goalReviewConfig: {
        enabled: false,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
        responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      },
      coreValueReviewConfig: {
        enabled: false,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
        responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      },
    };
  }
  if (stepType === ComplianceProgramStep.UpwardReview) {
    return {
      stepType: ComplianceProgramStep.UpwardReview,
      questionSetIds: [],
      anonymity: AssessmentGroupAnonymity.NotAnonymous,
      talentIndicatorReviewConfig: {
        sectionTitle: "",
        sectionDescription: "",
        talentIndicators: [],
      },
    };
  }
  if (stepType === ComplianceProgramStep.DownwardReview) {
    return {
      stepType: ComplianceProgramStep.DownwardReview,
      questionSetIds: [],
      roleBasedReviewConfig: {
        enabledForCurrentRole: false,
        enabledForNextRole: false,
        enabledSelfReviewForNextRole: false,
        competencyAssessmentType: CompetencyAssessmentType.CriteriaCombined,
        nextRoleCompetencyAssessmentType:
          CompetencyAssessmentType.CriteriaCombined,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
      },
      goalReviewConfig: {
        enabled: false,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
        responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      },
      coreValueReviewConfig: {
        enabled: false,
        assessmentScale: 3,
        assessmentScaleStartsAt: 1,
        assessmentScaleLabels: ["", "", ""],
        responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      },
      talentIndicatorReviewConfig: {
        sectionTitle: "",
        sectionDescription: "",
        talentIndicators: [],
      },
    };
  }
  if (stepType === ComplianceProgramStep.EngagementSurvey) {
    return {
      stepType: ComplianceProgramStep.EngagementSurvey,
      questionSetIds: [],
      anonymity: AssessmentGroupAnonymity.NotAnonymous,
    };
  }

  return {
    stepType: stepType as
      | ComplianceProgramStep.PreCalibration
      | ComplianceProgramStep.SelfReview
      | ComplianceProgramStep.PostCalibration
      | ComplianceProgramStep.Delivery
      | ComplianceProgramStep.OneOnOne,
  };
};

const getTalentIndicatorConfigFromTemplate = (
  talentIndicators: OrganizationTalentIndicatorFragment[],
  templateQuestionSets: {
    edges: ReadonlyArray<
      Maybe<{
        node?: Maybe<{
          talentIndicatorQuestionSettings?: Maybe<{
            title?: Maybe<string>;
            description?: Maybe<string>;
            talentIndicators?: ReadonlyArray<
              Maybe<TalentIndicatorQuestionSettingsNode>
            >;
          }>;
        }>;
      }>
    >;
  },
  isDuplicate = false,
): ProgramFormTalentIndicatorReviewConfig => {
  const sectionSettings = omit(
    templateQuestionSets.edges.find(
      (edge) => edge?.node?.talentIndicatorQuestionSettings,
    )?.node?.talentIndicatorQuestionSettings,
    ["__typename"],
  );
  return templateQuestionSets.edges.some(
    (edge) => edge?.node?.talentIndicatorQuestionSettings,
  )
    ? {
        sectionTitle: sectionSettings.title ?? "",
        sectionDescription: sectionSettings.description ?? "",
        talentIndicators: compact(
          templateQuestionSets.edges.find(
            (edge) => edge?.node?.talentIndicatorQuestionSettings,
          )?.node?.talentIndicatorQuestionSettings?.talentIndicators,
        ).map((indicator) => {
          return {
            ...omit(indicator, ["__typename"]),
            description: isDuplicate
              ? assertNonNull(
                  talentIndicators.find(
                    (orgIndicator) =>
                      orgIndicator.id === indicator.talentIndicatorId,
                  ),
                ).description
              : indicator.description,
          };
        }),
      }
    : { talentIndicators: [], sectionTitle: "", sectionDescription: "" };
};

const getStepsFromComplianceProgram = (
  isDuplicate: boolean,
  query: GetComplianceProgramEditQuery,
  sharingEnabled: boolean,
): ProgramFormStep[] => {
  const steps: ProgramFormStep[] = [];
  const program = query.complianceProgram;
  if (!program) {
    return steps;
  }

  const topicTemplates = assertEdgesNonNull(program.requiredTopicTemplates);
  const talentIndicators = assertEdgesNonNull(query.talentIndicators);

  if (topicTemplates.length > 0) {
    const oneOnOneStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.OneOnOne,
    );
    steps.push({
      stepType: ComplianceProgramStep.OneOnOne,
      topicTemplateId: topicTemplates[0].id,
      dueDateOffset: oneOnOneStep?.dueDateOffset
        ? `${oneOnOneStep.dueDateOffset}`
        : undefined,
    });
  }

  if (program.engagementSurveyTemplate) {
    steps.push({
      stepType: ComplianceProgramStep.EngagementSurvey,
      templateId: isDuplicate ? undefined : program.engagementSurveyTemplate.id,
      questionSetIds: program.engagementSurveyTemplate.questionSets.edges.map(
        (edge) => assertNonNull(edge?.node?.id),
      ),
      anonymity: program.engagementSurveyTemplate.anonymity,
    });
  }

  if (program.managerAssessmentTemplate) {
    const upwardStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.UpwardReview,
    );
    steps.push({
      stepType: ComplianceProgramStep.UpwardReview,
      templateId: isDuplicate
        ? undefined
        : program.managerAssessmentTemplate.id,
      dueDateOffset: upwardStep?.dueDateOffset
        ? `${upwardStep.dueDateOffset}`
        : undefined,
      questionSetIds: program.managerAssessmentTemplate.questionSets.edges
        .filter((edge) => !edge?.node?.talentIndicatorQuestionSettings)
        .map((edge) => assertNonNull(edge?.node?.id)),
      anonymity: program.managerAssessmentTemplate.anonymity,
      talentIndicatorReviewConfig: getTalentIndicatorConfigFromTemplate(
        talentIndicators,
        program.managerAssessmentTemplate.questionSets,
        isDuplicate,
      ),
    });
  }

  if (program.peerAssessmentTemplate) {
    const peerStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.PeerReview,
    );
    steps.push({
      stepType: ComplianceProgramStep.PeerReview,
      templateId: isDuplicate ? undefined : program.peerAssessmentTemplate.id,
      dueDateOffset: peerStep?.dueDateOffset
        ? `${peerStep.dueDateOffset}`
        : undefined,
      questionSetIds: program.peerAssessmentTemplate.questionSets.edges
        .filter((edge) => !edge?.node?.talentIndicatorQuestionSettings)
        .map((edge) => assertNonNull(edge?.node?.id)),
      providers: program.peerAssessmentTemplate.providers,
      anonymity: program.peerAssessmentTemplate.anonymity,
      roleBasedReviewConfig: {
        enabledForCurrentRole:
          program.peerAssessmentTemplate.enableRoleBasedAssessments,
        enabledForNextRole:
          program.peerAssessmentTemplate.roleBasedAssessmentsAssessNextRole,
        enabledSelfReviewForNextRole:
          program.peerAssessmentTemplate.roleBasedAssessmentsSelfAssessNextRole,
        competencyAssessmentType:
          program.peerAssessmentTemplate.competencyAssessmentType,
        nextRoleCompetencyAssessmentType:
          program.peerAssessmentTemplate.nextRoleCompetencyAssessmentType,
        assessmentScale:
          program.peerAssessmentTemplate.roleBasedAssessmentScale,
        assessmentScaleStartsAt:
          program.peerAssessmentTemplate.roleBasedAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.peerAssessmentTemplate.roleBasedAssessmentScaleLabels.filter(
            (s) => isString(s),
          ),
      },
      goalReviewConfig: {
        enabled: program.peerAssessmentTemplate.enableGoalAssessments,
        assessmentScale: program.peerAssessmentTemplate.goalAssessmentScale,
        assessmentScaleStartsAt:
          program.peerAssessmentTemplate.goalAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.peerAssessmentTemplate.goalAssessmentScaleLabels.filter((s) =>
            isString(s),
          ),
        responses: program.peerAssessmentTemplate.goalAssessmentResponses,
      },
      coreValueReviewConfig: {
        enabled: program.peerAssessmentTemplate.enableCoreValueAssessments,
        assessmentScale:
          program.peerAssessmentTemplate.coreValueAssessmentScale,
        assessmentScaleStartsAt:
          program.peerAssessmentTemplate.coreValueAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.peerAssessmentTemplate.coreValueAssessmentScaleLabels.filter(
            (s) => isString(s),
          ),
        responses: program.peerAssessmentTemplate.coreValueAssessmentResponses,
      },
    });
  }

  if (program.performanceAssessmentTemplate) {
    const selfReviewStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.SelfReview,
    );
    const downwardStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.DownwardReview,
    );
    steps.push({
      stepType: ComplianceProgramStep.SelfReview,
      dueDateOffset: selfReviewStep?.dueDateOffset
        ? `${selfReviewStep.dueDateOffset}`
        : undefined,
    });
    steps.push({
      stepType: ComplianceProgramStep.DownwardReview,
      templateId: isDuplicate
        ? undefined
        : program.performanceAssessmentTemplate.id,
      dueDateOffset: downwardStep?.dueDateOffset
        ? `${downwardStep.dueDateOffset}`
        : undefined,
      questionSetIds: program.performanceAssessmentTemplate.questionSets.edges
        .filter((edge) => !edge?.node?.talentIndicatorQuestionSettings)
        .map((edge) => assertNonNull(edge?.node?.id)),
      talentIndicatorReviewConfig: getTalentIndicatorConfigFromTemplate(
        talentIndicators,
        program.performanceAssessmentTemplate.questionSets,
        isDuplicate,
      ),
      roleBasedReviewConfig: {
        enabledForCurrentRole:
          program.performanceAssessmentTemplate.enableRoleBasedAssessments,
        enabledForNextRole:
          program.performanceAssessmentTemplate
            .roleBasedAssessmentsAssessNextRole,
        enabledSelfReviewForNextRole:
          program.performanceAssessmentTemplate
            .roleBasedAssessmentsSelfAssessNextRole,
        competencyAssessmentType:
          program.performanceAssessmentTemplate.competencyAssessmentType,
        nextRoleCompetencyAssessmentType:
          program.performanceAssessmentTemplate
            .nextRoleCompetencyAssessmentType,
        assessmentScale:
          program.performanceAssessmentTemplate.roleBasedAssessmentScale,
        assessmentScaleStartsAt:
          program.performanceAssessmentTemplate
            .roleBasedAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.performanceAssessmentTemplate.roleBasedAssessmentScaleLabels.filter(
            (s) => isString(s),
          ),
      },
      goalReviewConfig: {
        enabled: program.performanceAssessmentTemplate.enableGoalAssessments,
        assessmentScale:
          program.performanceAssessmentTemplate.goalAssessmentScale,
        assessmentScaleStartsAt:
          program.performanceAssessmentTemplate.goalAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.performanceAssessmentTemplate.goalAssessmentScaleLabels.filter(
            (s) => isString(s),
          ),
        responses:
          program.performanceAssessmentTemplate.goalAssessmentResponses,
      },
      coreValueReviewConfig: {
        enabled:
          program.performanceAssessmentTemplate.enableCoreValueAssessments,
        assessmentScale:
          program.performanceAssessmentTemplate.coreValueAssessmentScale,
        assessmentScaleStartsAt:
          program.performanceAssessmentTemplate
            .coreValueAssessmentScaleStartsAt,
        assessmentScaleLabels:
          program.performanceAssessmentTemplate.coreValueAssessmentScaleLabels.filter(
            (s) => isString(s),
          ),
        responses:
          program.performanceAssessmentTemplate.coreValueAssessmentResponses,
      },
    });
  }

  if (sharingEnabled) {
    const deliveryStep = program.steps.find(
      (s) => s?.step === ComplianceProgramStep.Delivery,
    );
    steps.push({
      stepType: ComplianceProgramStep.Delivery,
      dueDateOffset: deliveryStep?.dueDateOffset
        ? `${deliveryStep.dueDateOffset}`
        : undefined,
    });
  }
  return steps;
};

const shiftDate = (date: string | undefined, days: number) =>
  date ? moment(date).add(days, "days").format("YYYY-MM-DD") : date;

export const convertComplianceProgramToForm = (
  orgId: number,
  isDuplicate: boolean,
  program: GetComplianceProgramEditQuery,
  today: string = moment().format("YYYY-MM-DD"),
): ProgramForm => {
  const sourceEvaluationPeriod = getEvaluationPeriodType(
    program.complianceProgram,
  );
  const sourceStartDate = program.complianceProgram?.startDate ?? undefined;
  const sourceDueDate = program.complianceProgram?.dueDate ?? undefined;
  // A copy starts today and keeps the source's length. Step due dates, reminders
  // and the relative evaluation periods are offsets from the start date, so they
  // follow on their own. Only the fixed dates of a CUSTOM period move here.
  const daysShift =
    isDuplicate && sourceStartDate
      ? moment(today).diff(sourceStartDate, "days")
      : 0;
  const evaluationPeriod =
    sourceEvaluationPeriod.type === ProgramEvaluationPeriodType.CUSTOM
      ? {
          ...sourceEvaluationPeriod,
          startDate: shiftDate(sourceEvaluationPeriod.startDate, daysShift),
          endDate: shiftDate(sourceEvaluationPeriod.endDate, daysShift),
        }
      : sourceEvaluationPeriod;
  const assessmentTemplates = compact([
    program.complianceProgram?.performanceAssessmentTemplate,
    program.complianceProgram?.managerAssessmentTemplate,
    program.complianceProgram?.peerAssessmentTemplate,
  ]);
  const sharingEnabled = assessmentTemplates.some(
    (template) => template.delivery !== AssessmentGroupDelivery.None,
  );

  return {
    id: isDuplicate ? undefined : program.complianceProgram?.id,
    organizationId: orgId,
    title: isDuplicate
      ? `Copy of ${program.complianceProgram?.title ?? ""}`
      : (program.complianceProgram?.title ?? ""),
    state:
      isDuplicate || !program.complianceProgram?.state
        ? ComplianceProgramState.Draft
        : program.complianceProgram.state,
    // A copy of a recurring review would otherwise start a second repeating
    // series. The admin can still choose a recurrence in the form.
    recurrence: isDuplicate
      ? ComplianceProgramRecurrence.None
      : (program.complianceProgram?.recurrence ??
        ComplianceProgramRecurrence.None),
    ongoing: !!program.complianceProgram?.ongoing,
    startDate: shiftDate(sourceStartDate, daysShift),
    dueDate: shiftDate(sourceDueDate, daysShift),
    startDateOffset: program.complianceProgram?.startDateOffset,
    startDateDueDateOffset: program.complianceProgram?.startDateDueDateOffset,
    evaluationPeriod,
    startDatePeriodStartOffset:
      program.complianceProgram?.startDatePeriodStartOffset,
    startDatePeriodEndOffset:
      program.complianceProgram?.startDatePeriodEndOffset,
    sharesWithSubject: sharingEnabled,
    calibration:
      program.complianceProgram?.calibration ??
      ComplianceProgramCalibrationType.Off,
    appliesTo:
      program.complianceProgram?.appliesTo ??
      ComplianceProgramAppliesTo.Organization,
    appliesToTeams: compact(
      program.complianceProgram?.appliesToTeams.edges.map((edge) =>
        edge?.node ? { id: edge.node.id, title: edge.node.title } : null,
      ),
    ),
    appliesToManagers: compact(
      program.complianceProgram?.appliesToManagers.edges.map((edge) =>
        edge?.node ? { id: edge.node.id, name: edge.node.name } : null,
      ),
    ),
    appliesToUsers: compact(
      program.complianceProgram?.appliesToUsers.edges.map((edge) =>
        edge?.node ? { id: edge.node.id, name: edge.node.name } : null,
      ),
    ),
    excludedUsers: compact(
      program.complianceProgram?.excludedUsers.edges.map((edge) =>
        edge?.node ? { id: edge.node.id, name: edge.node.name } : null,
      ),
    ),
    // Recurring copies move the hire-date cutoff with the other dates, so a
    // copy does too.
    excludedHireDateAfter: shiftDate(
      program.complianceProgram?.excludedHireDateAfter ?? undefined,
      daysShift,
    ),
    scheduledReminders: assertEdgesNonNull(
      program.complianceProgram?.scheduledReminders,
    )
      // Copy scheduled reminders, including sent ones, but not manual send history.
      // Drop copied ids so the backend creates new reminders.
      .filter(({ sentDatetime, sendOnUserLocalTime }) =>
        isDuplicate ? sendOnUserLocalTime : sentDatetime === null,
      )
      .map((reminder) =>
        isDuplicate
          ? omit(
              reminder,
              "__typename",
              "sentDatetime",
              "sendOnUserLocalTime",
              "id",
            )
          : omit(reminder, "__typename", "sentDatetime", "sendOnUserLocalTime"),
      ),
    steps: getStepsFromComplianceProgram(isDuplicate, program, sharingEnabled),
  };
};

const getAssessmentTemplatesFromSteps = (
  steps: {
    peerStep?: ProgramFormStepPeerReview;
    upwardStep?: ProgramFormStepUpwardReview;
    downwardStep?: ProgramFormStepDownwardReview;
    surveyStep?: ProgramFormStepSurvey;
  },
  sharesWithSubject: boolean,
  calibration: ComplianceProgramCalibrationType,
): AssessmentTemplateComplianceRequirement[] => {
  const assessmentTemplates: AssessmentTemplateComplianceRequirement[] = [];

  const { peerStep, downwardStep, upwardStep, surveyStep } = steps;

  if (surveyStep) {
    return [
      {
        id: surveyStep.templateId,
        assessmentType: AssessmentType.EngagementSurvey,
        questionSetIds: surveyStep.questionSetIds,
        anonymity: surveyStep.anonymity,
      },
    ];
  }

  if (peerStep) {
    assessmentTemplates.push({
      id: peerStep.templateId,
      assessmentType: AssessmentType.Peer,
      questionSetIds: peerStep.questionSetIds,
      providers: peerStep.providers,
      anonymity: peerStep.anonymity,
      enableCoreValueAssessments: peerStep.coreValueReviewConfig.enabled,
      enableGoalAssessments: peerStep.goalReviewConfig.enabled,
      enableRoleBasedAssessments:
        peerStep.roleBasedReviewConfig.enabledForCurrentRole,
      roleBasedAssessmentsAssessNextRole:
        peerStep.roleBasedReviewConfig.enabledForNextRole,
      roleBasedAssessmentsSelfAssessNextRole:
        peerStep.roleBasedReviewConfig.enabledSelfReviewForNextRole,
      competencyAssessmentType:
        peerStep.roleBasedReviewConfig.competencyAssessmentType,
      nextRoleCompetencyAssessmentType:
        peerStep.roleBasedReviewConfig.nextRoleCompetencyAssessmentType,
      roleBasedAssessmentScale: peerStep.roleBasedReviewConfig.assessmentScale,
      roleBasedAssessmentScaleLabels:
        peerStep.roleBasedReviewConfig.assessmentScaleLabels,
      roleBasedAssessmentScaleStartsAt:
        peerStep.roleBasedReviewConfig.assessmentScaleStartsAt,
      coreValueAssessmentResponses: peerStep.coreValueReviewConfig.responses,
      coreValueAssessmentScale: peerStep.coreValueReviewConfig.assessmentScale,
      coreValueAssessmentScaleLabels:
        peerStep.coreValueReviewConfig.assessmentScaleLabels,
      coreValueAssessmentScaleStartsAt:
        peerStep.coreValueReviewConfig.assessmentScaleStartsAt,
      goalAssessmentResponses: peerStep.goalReviewConfig.responses,
      goalAssessmentScale: peerStep.goalReviewConfig.assessmentScale,
      goalAssessmentScaleLabels:
        peerStep.goalReviewConfig.assessmentScaleLabels,
      goalAssessmentScaleStartsAt:
        peerStep.goalReviewConfig.assessmentScaleStartsAt,
    });
  }
  if (downwardStep) {
    assessmentTemplates.push({
      id: downwardStep.templateId,
      assessmentType: AssessmentType.Performance,
      questionSetIds: downwardStep.questionSetIds,
      enableCoreValueAssessments: downwardStep.coreValueReviewConfig.enabled,
      enableGoalAssessments: downwardStep.goalReviewConfig.enabled,
      enableRoleBasedAssessments:
        downwardStep.roleBasedReviewConfig.enabledForCurrentRole,
      roleBasedAssessmentsAssessNextRole:
        downwardStep.roleBasedReviewConfig.enabledForNextRole,
      roleBasedAssessmentsSelfAssessNextRole:
        downwardStep.roleBasedReviewConfig.enabledSelfReviewForNextRole,
      competencyAssessmentType:
        downwardStep.roleBasedReviewConfig.competencyAssessmentType,
      nextRoleCompetencyAssessmentType:
        downwardStep.roleBasedReviewConfig.nextRoleCompetencyAssessmentType,
      roleBasedAssessmentScale:
        downwardStep.roleBasedReviewConfig.assessmentScale,
      roleBasedAssessmentScaleLabels:
        downwardStep.roleBasedReviewConfig.assessmentScaleLabels,
      roleBasedAssessmentScaleStartsAt:
        downwardStep.roleBasedReviewConfig.assessmentScaleStartsAt,
      coreValueAssessmentResponses:
        downwardStep.coreValueReviewConfig.responses,
      coreValueAssessmentScale:
        downwardStep.coreValueReviewConfig.assessmentScale,
      coreValueAssessmentScaleLabels:
        downwardStep.coreValueReviewConfig.assessmentScaleLabels,
      coreValueAssessmentScaleStartsAt:
        downwardStep.coreValueReviewConfig.assessmentScaleStartsAt,
      goalAssessmentResponses: downwardStep.goalReviewConfig.responses,
      goalAssessmentScale: downwardStep.goalReviewConfig.assessmentScale,
      goalAssessmentScaleLabels:
        downwardStep.goalReviewConfig.assessmentScaleLabels,
      goalAssessmentScaleStartsAt:
        downwardStep.goalReviewConfig.assessmentScaleStartsAt,
      talentIndicatorConfig: {
        title: downwardStep.talentIndicatorReviewConfig.sectionTitle,
        description:
          downwardStep.talentIndicatorReviewConfig.sectionDescription,
        talentIndicators:
          downwardStep.talentIndicatorReviewConfig.talentIndicators,
      },
    });
  }
  if (upwardStep) {
    assessmentTemplates.push({
      id: upwardStep.templateId,
      assessmentType: AssessmentType.Manager,
      questionSetIds: upwardStep.questionSetIds,
      anonymity: upwardStep.anonymity,
      talentIndicatorConfig: {
        title: upwardStep.talentIndicatorReviewConfig.sectionTitle,
        description: upwardStep.talentIndicatorReviewConfig.sectionDescription,
        talentIndicators:
          upwardStep.talentIndicatorReviewConfig.talentIndicators,
      },
    });
  }

  if (!sharesWithSubject) {
    return assessmentTemplates.map((template) => ({
      ...template,
      delivery: AssessmentGroupDelivery.None,
    }));
  }

  if (calibration !== ComplianceProgramCalibrationType.PostCalibration) {
    return assessmentTemplates.map((template) => ({
      ...template,
      delivery: AssessmentGroupDelivery.Partial,
    }));
  }

  return assessmentTemplates.map((template) => ({
    ...template,
    delivery:
      template.assessmentType === AssessmentType.Performance
        ? AssessmentGroupDelivery.AdminApproval
        : AssessmentGroupDelivery.Partial,
  }));
};

export const convertProgramFormToMutationVariables = (
  program: ProgramForm,
  shouldIncludeTemplates: boolean,
): SaveComplianceProgramMutationVariables => {
  const evaluationPeriod = convertEvaluationPeriodTypeToPeriod(
    program.evaluationPeriod,
    program.startDate,
  );

  const oneOnOneStep = program.steps.find(
    (step) => step.stepType === ComplianceProgramStep.OneOnOne,
  );
  const peerStep = program.steps.find(
    (step) => step.stepType === ComplianceProgramStep.PeerReview,
  );
  const upwardStep = program.steps.find(
    (step) => step.stepType === ComplianceProgramStep.UpwardReview,
  );
  const downwardStep = program.steps.find(
    (step) => step.stepType === ComplianceProgramStep.DownwardReview,
  );
  const surveyStep = program.steps.find(
    (step) => step.stepType === ComplianceProgramStep.EngagementSurvey,
  );

  return {
    complianceProgramId: program.id,
    organizationId: program.organizationId,
    title: program.title,
    state: program.state,
    recurrence: program.recurrence,
    ongoing: program.ongoing,
    startDate: program.startDate,
    dueDate: program.dueDate,
    startDateOffset: program.startDateOffset,
    startDateDueDateOffset: program.startDateDueDateOffset,
    periodStartDate: evaluationPeriod.periodStartDate,
    periodEndDate: evaluationPeriod.periodEndDate,
    startDatePeriodStartOffset: program.startDatePeriodStartOffset,
    startDatePeriodEndOffset: program.startDatePeriodEndOffset,
    calibration: program.calibration,
    appliesTo: program.appliesTo,
    appliesToTeams: program.appliesToTeams.map(({ id }) => id),
    appliesToManagers: program.appliesToManagers.map(({ id }) => id),
    appliesToUsers: program.appliesToUsers.map(({ id }) => id),
    excludedUsers: program.excludedUsers.map(({ id }) => id),
    excludedHireDateAfter: program.excludedHireDateAfter,
    removeExcludedHireDateAfter: program.excludedHireDateAfter
      ? undefined
      : true,
    scheduledReminders: program.scheduledReminders,
    steps: compact(
      program.steps.map((step) =>
        "dueDateOffset" in step && isInteger(Number(step.dueDateOffset))
          ? {
              step: step.stepType,
              dueDateOffset: Number(step.dueDateOffset),
            }
          : undefined,
      ),
    ),
    topicTemplates:
      shouldIncludeTemplates && oneOnOneStep && oneOnOneStep.topicTemplateId
        ? [
            {
              id: oneOnOneStep.topicTemplateId,
            },
          ]
        : undefined,
    assessmentTemplates: shouldIncludeTemplates
      ? getAssessmentTemplatesFromSteps(
          { peerStep, downwardStep, upwardStep, surveyStep },
          program.sharesWithSubject,
          program.calibration,
        )
      : undefined,
  };
};

// A review that nobody gave dates yet runs for this long from its start date,
// and the server applies the same defaults. A conditional review kicks off this
// many days after somebody starts, and is due the same number of days after.
export const DEFAULT_REVIEW_LENGTH_DAYS = 30;
export const DEFAULT_ONGOING_OFFSET_DAYS = 30;

export const getDefaultProgramDates = (
  today: string = moment().format("YYYY-MM-DD"),
) => ({
  startDate: today,
  dueDate: moment(today)
    .add(DEFAULT_REVIEW_LENGTH_DAYS, "days")
    .format("YYYY-MM-DD"),
});

export const getDefaultOngoingOffsets = () => ({
  startDateOffset: DEFAULT_ONGOING_OFFSET_DAYS,
  startDateDueDateOffset: DEFAULT_ONGOING_OFFSET_DAYS * 2,
});

// Publishing a single-cycle review whose start date has passed kicks it off
// there and then, which an admin may not expect from a draft saved weeks ago.
export const isKickoffDatePast = (
  program: Pick<ProgramForm, "ongoing" | "startDate">,
  today: string = moment().format("YYYY-MM-DD"),
) =>
  !program.ongoing &&
  !!program.startDate &&
  moment(program.startDate).isBefore(today, "day");

export type ProgramSaveMode = "draft" | "publish";

// Saving a draft needs a name and dates in the right order. Missing questions,
// participants or an evaluation period are checked when publishing, and the
// server checks them again, because the review page's Publish action does not
// go through this form.
export const validateProgramForm = (
  program: ProgramForm,
  mode: ProgramSaveMode,
  today: string = moment().format("YYYY-MM-DD"),
): FormError[] => {
  const errors: FormError[] = [];
  const isPublish = mode === "publish";
  // A survey has no evaluation period: the Workflow tab hides those fields, and
  // the server does not ask for one either.
  const asksForEvaluationPeriod = !program.steps.some(
    (step) => step.stepType === ComplianceProgramStep.EngagementSurvey,
  );

  if (!program.title.trim()) {
    errors.push({
      field: "title",
      message: "Title cannot be blank",
    });
  }

  if (program.ongoing) {
    if (isPublish && !program.startDateOffset) {
      errors.push({
        field: "startDateOffset",
        message: "Please enter a start date",
      });
    }
    if (isPublish && !program.startDateDueDateOffset) {
      errors.push({
        field: "startDateDueDateOffset",
        message: "Please enter a due date",
      });
    }
    if (
      program.startDateOffset &&
      program.startDateDueDateOffset &&
      program.startDateOffset >= program.startDateDueDateOffset
    ) {
      errors.push({
        field: "startDateOffset",
        message: "Due date must be after start date",
      });
    }
    if (
      asksForEvaluationPeriod &&
      isPublish &&
      !program.startDatePeriodStartOffset
    ) {
      errors.push({
        field: "startDatePeriodStartOffset",
        message: "Please enter an evaluation period start date",
      });
    }
    if (
      asksForEvaluationPeriod &&
      isPublish &&
      !program.startDatePeriodEndOffset
    ) {
      errors.push({
        field: "startDatePeriodEndOffset",
        message: "Please enter an evaluation period end date",
      });
    }
    if (
      asksForEvaluationPeriod &&
      program.startDatePeriodStartOffset &&
      program.startDatePeriodEndOffset &&
      program.startDatePeriodStartOffset >= program.startDatePeriodEndOffset
    ) {
      errors.push({
        field: "startDatePeriodStartOffset",
        message: "Evaluation period end date must be after start date",
      });
    }
  } else {
    if (isPublish && !program.dueDate) {
      errors.push({
        field: "dueDate",
        message: "Please enter a due date",
      });
    }
    if (isPublish && !program.startDate) {
      errors.push({
        field: "startDate",
        message: "Please enter a start date",
      });
    }
    if (
      program.startDate &&
      program.dueDate &&
      moment(program.startDate).isAfter(program.dueDate)
    ) {
      errors.push({
        field: "startDate",
        message: "Due date must be after start date",
      });
    }
    // A draft's default dates were set when it was first saved. Only a draft
    // being published is held to this: a running review may be past due.
    if (
      isPublish &&
      program.state === ComplianceProgramState.Draft &&
      program.dueDate &&
      moment(program.dueDate).isBefore(today, "day")
    ) {
      errors.push({
        field: "dueDate",
        message: "The due date has passed. Choose a later one",
      });
    }

    const evaluationPeriod = convertEvaluationPeriodTypeToPeriod(
      program.evaluationPeriod,
      program.startDate,
    );
    if (
      asksForEvaluationPeriod &&
      isPublish &&
      !evaluationPeriod.periodStartDate
    ) {
      errors.push({
        field: "periodStartDate",
        message: "Please enter an evaluation period start date",
      });
    }
    if (
      asksForEvaluationPeriod &&
      isPublish &&
      !evaluationPeriod.periodEndDate
    ) {
      errors.push({
        field: "periodEndDate",
        message: "Please enter an evaluation period end date",
      });
    }
    if (
      asksForEvaluationPeriod &&
      evaluationPeriod.periodStartDate &&
      evaluationPeriod.periodEndDate &&
      moment(evaluationPeriod.periodStartDate).isAfter(
        evaluationPeriod.periodEndDate,
      )
    ) {
      errors.push({
        field: "periodStartDate",
        message: "Evaluation period end date must be after start date",
      });
    }
  }

  if (!isPublish) {
    return errors;
  }

  // Only a hand-picked list can be seen to be empty here. Whether a rule such
  // as "direct reports" names anybody is for the server to say when publishing.
  const pickedParticipantsByRule: Partial<
    Record<ComplianceProgramAppliesTo, unknown[]>
  > = {
    [ComplianceProgramAppliesTo.Departments]: program.appliesToTeams,
    [ComplianceProgramAppliesTo.ReportsTo]: program.appliesToManagers,
    [ComplianceProgramAppliesTo.Users]: program.appliesToUsers,
  };
  const pickedParticipants = pickedParticipantsByRule[program.appliesTo];
  if (pickedParticipants && pickedParticipants.length === 0) {
    errors.push({
      field: "appliesTo",
      message: "Choose at least one participant",
    });
  }

  program.steps.forEach((step) => {
    if ("questionSetIds" in step && step.questionSetIds.length === 0) {
      errors.push({
        field: "questionSetIds",
        message: "Please choose a question set for all steps",
      });
    }
    if (
      step.stepType === ComplianceProgramStep.OneOnOne &&
      !step.topicTemplateId
    ) {
      errors.push({
        field: "topicTemplateId",
        message: "Please choose a 1-on-1 template",
      });
    }
  });

  return errors;
};
